import { useCallback, useMemo, useState } from 'react';
import type { AccountFormTagFields } from '../accountFormTags';

export interface RealEstateDetailsFormValues extends AccountFormTagFields {
  Name: string;
  Notes: string;
  AccountSubType: string;
  Address: string;
  PropertyValue: string;
  Zpid: string;
  ClassificationId: number | undefined;
  ParentId: number | undefined;
}

export interface RealEstateDetailsFormState {
  values: RealEstateDetailsFormValues;
  isValid: boolean;
}

// 1. Move static values outside the hook to keep them stable
const INITIAL_VALUES: RealEstateDetailsFormValues = {
  Name: '',
  Notes: '',
  AccountSubType: '',
  Address: '',
  PropertyValue: '',
  Zpid: '',
  ClassificationId: undefined,
  ParentId: undefined,
  SelectedTagIds: [],
};

const INITIAL_STATE: RealEstateDetailsFormState = {
  values: INITIAL_VALUES,
  isValid: false,
};

const useRealEstateDetailsForm = () => {
  const [realEstateDetailsForm, setRealEstateDetailsForm] =
    useState<RealEstateDetailsFormState>(INITIAL_STATE);

  // 2. Wrap "update" in useCallback
  // This ensures the function reference stays the same
  const updateRealEstateDetailForm = useCallback(
    (state: RealEstateDetailsFormState) => {
      setRealEstateDetailsForm(state);
    },
    [],
  );

  // 3. Wrap "reset" in useCallback
  const resetRealEstateDetailsForm = useCallback(() => {
    setRealEstateDetailsForm(INITIAL_STATE);
  }, []);

  // 4. Memoize the returned object
  // This prevents components using this hook from re-rendering unless the form data actually changes
  return useMemo(
    () => ({
      resetRealEstateDetailsForm,
      realEstateDetailsForm,
      updateRealEstateDetailForm,
    }),
    [
      realEstateDetailsForm,
      resetRealEstateDetailsForm,
      updateRealEstateDetailForm,
    ],
  );
};

export default useRealEstateDetailsForm;
